import _ from 'lodash';
import React, { Component } from 'react';
import Autosuggest from 'react-autosuggest';
import { FormCheck } from 'react-bootstrap';
import Highlighter from 'react-highlight-words';
import toastr from '../../../toastr';

import { Api, IApiResponse } from '../../typings/Api';

interface IPartLookupRecord {
  readonly Description: string;
  readonly 'On Hand': string;
  readonly 'Price Symbol': string;
  readonly 'Stock Class': string;
  readonly itemid: string;
  readonly super_type: 'now' | 'delayed' | null;
}

interface IProps {
  loadPartEntryFromLookup: Function;
  highlightSupercededSearch: boolean;
}

interface IState {
  fullCatalog: boolean;
  suggestions: IPartLookupRecord[];
  value: string;
}

class PartLookup extends Component<IProps, IState> {
  id = 'partLookupSK';

  state = {
    fullCatalog: false,
    suggestions: [],
    value: '',
  };

  // For use via an external component ref
  set value(value: string) {
    this.setState({ value }, () => {
      // advance cursor to end of input for continued typing
      setTimeout(() => {
        const el = document.getElementById(this.id) as HTMLInputElement;
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }, 100);
    });
  }

  clear = () => {
    this.setState({ fullCatalog: false, value: '' });
  };

  getSuggestions = async (value: string) => {
    const partList = this.state.fullCatalog ? 'PIM9' : 'PIV1';
    let res: IApiResponse;

    try {
      res = await Api.get(`/list/${partList}`, {
        params: {
          limit: 100,
          q: value.trim(),
          skipAttachments: true,
        },
      });

      if (res.status === 200) {
        this.setState({
          suggestions: res.data.list,
        });
      } else {
        throw new Error('200 not received from /list/PIV1');
      }
    } catch (err: any) {
      console.error(err.message);
      toastr.error('Error', 'Part search temporarily unavailable.');
      this.setState({
        suggestions: [],
      });
    }
  };

  getSuggestionsDebounced = _.debounce(this.getSuggestions, 250); // tslint:disable-line

  getSuggestionValue = (suggestion: IPartLookupRecord) => suggestion.itemid;

  onChange = (syntheticEvent: any, event: Autosuggest.ChangeEvent) => {
    this.setState({
      value: event.newValue,
    });
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
    });
  };

  onSuggestionsFetchRequested = (req: Autosuggest.SuggestionsFetchRequestedParams) => {
    // req.reason seems to no longer exist in 9.1.x, docs wrong
    // created issue: https://github.com/moroshko/react-autosuggest/issues/386
    this.getSuggestionsDebounced(req.value);
  };

  onSuggestionSelected = (syntheticEvent: any, selection: Autosuggest.SuggestionSelectedEventData<any>) => {
    // Set PartEntry field here with suggestion
    this.props.loadPartEntryFromLookup(selection.suggestionValue);
    syntheticEvent.preventDefault();
  };

  /* This is used to dynamically calculate the height/width of the suggestion container.

     By default, applying a 100% width to the container would constrain it to the size of its
     containing div.  In situations (like shopkeeper) where that div is part of a flex row of
     other divs, the width would be limited significantly.  This logic allows the width to be
     calculated based on the width of the viewport (sans scroll) and "break out" of said
     containing div.

     Specifically for width, it uses all width between the left edge of the container up to 15px
     shy of the right edge of the viewport (sans scroll).

     Specifically for height, the container will always be AT LEAST 150px (minHeight) tall, and
     AT MOST (maxHeight) a height equal to the distance from the top of the container, to the
     bottom of the viewport, minus 75px (roughly size of actionbar footer in shopkeeper).
  */
  renderSuggestionsContainer = ({ containerProps, children }: any) => {
    const style = {
      maxHeight: '0px',
      minHeight: '0px',
      width: '0px',
    };

    if (
      containerProps.className ===
      'react-autosuggest__suggestions-container react-autosuggest__suggestions-container--open'
    ) {
      const containerRect = (document.getElementById(containerProps.id) as HTMLElement).getBoundingClientRect();
      style.minHeight = '150px';
      style.maxHeight = `${document.body.clientHeight - containerRect.top - 75}px`;
      style.width = `${document.body.clientWidth - containerRect.left - 15}px`;
    }

    return (
      <div {...containerProps} style={style}>
        {children}
      </div>
    );
  };

  renderSuggestion = (suggestion: IPartLookupRecord, { query }: { query: string }) => {
    const description = suggestion.Description ? ` - ${suggestion.Description}` : '';

    let className = 'renderSuggestion';
    let textToHighlight = `${suggestion.itemid}${description}`;

    // PIV1 default part search
    if (!this.state.fullCatalog) {
      textToHighlight += ` / ${suggestion['On Hand']}`;
    }

    if (this.props.highlightSupercededSearch && suggestion.super_type === 'now') {
      className += ' suggestion-inactive';
      textToHighlight += ' - SUPERCEDED';
    }

    return (
      <div className={className}>
        <Highlighter
          autoEscape={true}
          highlightClassName="hl"
          searchWords={[query]}
          textToHighlight={textToHighlight}
        />
      </div>
    );
  };

  render() {
    const { value, suggestions } = this.state;

    const inputProps = {
      id: this.id,
      onChange: this.onChange,
      value,
    };

    return (
      <>
        <Autosuggest
          getSuggestionValue={this.getSuggestionValue}
          highlightFirstSuggestion={true}
          id={this.id}
          inputProps={inputProps}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionSelected={this.onSuggestionSelected}
          renderSuggestion={this.renderSuggestion}
          renderSuggestionsContainer={this.renderSuggestionsContainer}
          suggestions={suggestions}
        />

        <FormCheck inline={true}>
          <FormCheck.Input
            type="checkbox"
            id="searchFullCatalogCheckbox"
            className="sk-useMaterialInput ml-1 sk-searchFullCatalogOption"
            checked={this.state.fullCatalog}
            onChange={(evt: any) => this.setState({ fullCatalog: evt.target.checked })}
          />
          <FormCheck.Label className="sk-searchFullCatalogLabel" htmlFor="searchFullCatalog">
            Search Full Catalog
          </FormCheck.Label>
        </FormCheck>
      </>
    );
  }
}

export default PartLookup;
